import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@kidshome/ui';
import { LoadingState, ErrorState, EmptyState } from '@/shared/ui';
import { useCan } from '@/entities/session';
import { Perm } from '@/shared/lib/permission-codes';
import { useMembersGuarded } from '@/entities/member/use-members-guarded';
import { MemberCard } from '@/widgets/member-cards/MemberCard';

/**
 * "Our kids" section of the family dashboard. Lists the household's children (from `GET /v1/members`)
 * as at-a-glance cards. Guardians only — a child never sees siblings (the API scopes; the UI mirrors
 * by only mounting this in the family shell). When there are no kids yet, a guardian who can add one
 * gets a CTA straight into the add-child form instead of a dead end.
 *
 * A guardian whose role lacks `member:read` (a custom role may omit "View family") gets this whole
 * section quietly hidden (ADR-0137) rather than an alarming error box: the section's entire value is
 * showing who the kids are, so there's nothing honest left to render without the roster — same
 * treatment as a genuinely-empty household would get if it had a `return null` case (it doesn't; this
 * widget always mounts alongside other dashboard sections, so hiding it here just drops one card).
 */
export function MemberCards({
  showHeading = true,
}: { showHeading?: boolean } = {}): React.ReactElement | null {
  const { t } = useTranslation('family');
  const { can } = useCan();
  const members = useMembersGuarded();

  if (members.forbidden) return null;
  if (members.isPending) return <LoadingState />;
  if (members.isError) return <ErrorState onRetry={() => void members.refetch()} />;

  const kids = members.data.data.filter((m) => m.kind === 'child');
  if (kids.length === 0) {
    return (
      <EmptyState
        art="empty.children"
        glyph="👶"
        message={t('dashboard.noKids')}
        action={
          can(Perm.MemberManage) ? (
            <Button asChild size="sm">
              <Link to="/admin/members/new">{t('dashboard.noKidsCta')}</Link>
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {showHeading && (
        <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-text-muted">
          {t('dashboard.kidsTitle')}
        </h2>
      )}
      {kids.map((member) => (
        <MemberCard key={member.id} member={member} />
      ))}
    </div>
  );
}
